import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Uppsala?", a: "Ansökan görs till Miljöförvaltningen / tillståndsenheten i Uppsala kommun. Verksamhet i annan kommun i Uppsala län (t.ex. Knivsta, Sigtuna, Östhammar, Enköping) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Uppsala?", a: "Uppsala kommun tar ut en ansökningsavgift och en årlig tillsynsavgift. Avgifterna varierar beroende på tillståndstyp och alkoholförsäljningens storlek – kontrollera aktuella avgifter på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Uppsala?", a: "Uppsala kommun anger normalt 8–12 veckor för en komplett ansökan om stadigvarande tillstånd. Tillfälliga tillstånd kan gå snabbare." },
];

export const Route = createFileRoute("/serveringstillstand-uppsala")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Uppsala" },
      ]}
      title="Serveringstillstånd Uppsala – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Uppsala: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.uppsala, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      localData={{
        unit: "Uppsala kommun – Miljöförvaltningen, Tillståndsenheten",
        address: "Stadshusgatan 2, 753 75 Uppsala",
        phone: "018-727 00 00 (växel)",
        waitTime: "ca 8–12 veckor för stadigvarande tillstånd från komplett ansökan",
        feeExamples: [
          "Ansökningsavgift stadigvarande tillstånd: enligt kommunens taxa, betalas till bankgiro 344-6002",
          "Tillsynsavgift: fast del + rörlig del baserat på alkoholomsättning",
          "Se kommunens sida för aktuella belopp – de uppdateras löpande",
        ],
        checklist: [
          "Registreringsbevis",
          "Finansieringsplan och kontoutdrag",
          "Hyresavtal eller nyttjanderätt till lokalen",
          "Ritning över serveringsyta",
          "Brandskyddsdokumentation",
          "PBI-uppgifter och godkänt kunskapsprov",
        ],
        officialUrl: KOMMUN_LINKS.uppsala,
      }}
      sections={[
        { h2: "Var ansöker du i Uppsala?", body: "Miljöförvaltningen / tillståndsenheten i Uppsala kommun ansvarar för serveringstillstånd i Uppsala kommun. För verksamhet i kringliggande kommuner som Knivsta, Sigtuna, Östhammar, Enköping ansöker du hos respektive kommun. Varje kommun har egna blanketter och e-tjänster, men kraven enligt alkohollagen är nationella och gäller överallt." },
        { h2: "Krav från Uppsala kommun", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) kan kommunen ha lokala riktlinjer kring serveringstider, uteservering och ordning. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt. Uppsala har en stor studentpopulation vilket innebär många studentpubar och nationsverksamheter med särskilda regleringar." },
        { h2: "Kunskapsprovet i Uppsala", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Det är ett skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning och nykterhet, marknadsföring m.m. Frågeantalet varierar beroende på tillståndstyp och kommun (FoHMFS 2014:7). Med Alkoholprovet kan du träna i samma format hemifrån och gå in på provet förberedd." },
        { h2: "Handläggningstid i Uppsala", body: "Uppsala kommuns tillståndsenhet nås via serveringstillstand@uppsala.se eller växeln 018-727 00 00. Ansökan skickas till Uppsala kommun, Miljöförvaltningen – tillståndsenheten, Stadshusgatan 2, 753 75 Uppsala. Räkna med ca 8–12 veckor från komplett ansökan till beslut om stadigvarande tillstånd; en stor studentbefolkning och många nations- och pubverksamheter gör att handläggningen kan ta något längre tid inför terminsstart. Ofullständig ansökan eller komplicerad lämplighetsprövning förlänger tiden ytterligare." },
        { h2: "Tillsyn och uppföljning", body: "Uppsala kommun bedriver aktiv tillsyn på serveringsställen tillsammans med polis och Skatteverket. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd. Goda kunskaper i alkohollagen minskar risken för misstag i den dagliga driften." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-stockholm", label: "Serveringstillstånd Stockholm" },
          { to: "/serveringstillstand-goteborg", label: "Serveringstillstånd Göteborg" },
          { to: "/serveringstillstand-malmo", label: "Serveringstillstånd Malmö" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/serveringstillstand-bar", label: "För bar & nattklubb" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Uppsala: serveringstillstånd & kunskapsprov 2026" },
      { name: "description", content: "Komplett guide för dig som ska söka serveringstillstånd i Uppsala 2026 – krav, dokument och kunskapsprovet." },
      { property: "og:title", content: "Uppsala – tillstånd & kunskapsprov" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Uppsala." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-uppsala" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-uppsala" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Uppsala", url: "https://alkoholprovet.se/serveringstillstand-uppsala" },
      ]),
    ],
  }),
});
